import { z } from 'zod';

/**
 * Validated client env. Read ONCE at boot.
 *
 * Security: Vite exposes any `VITE_*` to the browser bundle. Treat every value
 * here as PUBLIC. Never put secrets, tokens, signing keys, or PII here.
 */
const booleanFromString = z
  .union([z.literal('true'), z.literal('false')])
  .transform((v) => v === 'true');

const EnvSchema = z.object({
  VITE_APP_ENV: z.enum(['development', 'staging', 'production']),
  VITE_ENABLE_DEVTOOLS: booleanFromString.default('false'),
  VITE_ENABLE_MSW: booleanFromString.default('false'),
  VITE_FEATURE_AIOPS: booleanFromString.default('false'),
  VITE_FEATURE_CAPITAL: booleanFromString.default('false'),
  // Safe mode (DEFAULT ON): hide the advanced/intrusive Intelligence surfaces (Workflows,
  // Execution Activity, local-DB integration tools) so the product shows the clean read-only
  // story by default. Nothing is removed from the code; set this to 'false' to reveal the
  // advanced surfaces. Pairs with backend PFL_HIDE_INTRUSIVE.
  VITE_INTELLIGENCE_SAFE_MODE: booleanFromString.default('true'),
  // Hide-WIP (DEFAULT ON): the single flag we gate not-yet-shipping / not-for-this-audience
  // UI behind. When 'true' (default) the gated bits are hidden; set to 'false' to reveal them.
  // Reuse `env.VITE_HIDE_WIP` at every spot we want hidden for the current release.
  VITE_HIDE_WIP: booleanFromString.default('true'),
  // Dev-only: a real backend Bearer token to use when MSW is off. Set in
  // .env.local; never committed. Optional now that the real login flow is wired.
  VITE_DEV_BEARER: z.string().optional(),
  // Base for the Verofy Connect (Node) auth API. The console authenticates here
  // (POST /auth/login, GET /user/me). Default is a same-origin path proxied to
  // connect :3333 by the Vite dev server (avoids browser CORS).
  VITE_CONNECT_AUTH_BASE: z.string().min(1).default('/connect-api'),
});

export type AppEnv = z.infer<typeof EnvSchema>;

function parseEnv(): AppEnv {
  const parsed = EnvSchema.safeParse(import.meta.env);
  if (!parsed.success) {
    // Fail loud and early. Never silently boot with bad config.
    const issues = parsed.error.issues
      .map((i) => `  - ${i.path.join('.')}: ${i.message}`)
      .join('\n');
    throw new Error(
      `Invalid environment configuration:\n${issues}\nSee .env.example for the contract.`,
    );
  }
  return parsed.data;
}

export const env: AppEnv = parseEnv();
